/**
 * Place only simple/testable (check ./cypress/e2e/functions-1.cy.ts) functions here.
 * For Angular/Rxjs dependent functions use ./functions-angular.ts!
 */
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { ECurrencyCode, EInstrumentTarget, IErrorOptionsInput } from 'src/models/common.model';
import { logError } from 'src/services/logger.service';
import { ERiskSort, IApiThematicStrategy, ICardViewThematicStrategy, IInstrumentDetailFullOut, IInstrumentsFilter, IPortfolioOverview, IPortfolioOverviewDisplayed, investmentGoalTranslateMap } from 'src/models/products.model';
import { ETransactionFilterPeriod, ITransaction, ITransactionGroup } from 'src/models/transactions.model';
import dayjs, { Dayjs } from 'dayjs';
import { Capacitor } from '@capacitor/core';
import { EApiDevicePlatforms, ECapacitorPlatforms, IApiHeadersGetterInput } from 'src/models/app.model';
import packageInfo from 'package.json';

export function parseToError(error: unknown): HttpErrorResponse | null {
  let parsedError: null | HttpErrorResponse = null;

  if(typeof error === 'function') {
    parsedError = error() as HttpErrorResponse;
  }
  if(error && typeof error === 'object') {
    parsedError = error as HttpErrorResponse;
  }
  if(!(error instanceof HttpErrorResponse)) {
    console.error('Passed object is not of type HttpErrorResponse!', error);
  }
  return parsedError;
}

/**
 * Dont use/deprecated, used as hotfix/sure check of RxJs error object after throwError changed in v7
 * https://rxjs.dev/deprecations/breaking-changes#throwerror
 * @output boolean - true if @param error is HttpErrorResponse-like and meets conditions from additional params
 */
export function isInputError(error: unknown, statusCode: number, options?: IErrorOptionsInput): boolean {
  if(
    typeof error === 'function' &&
    error() &&
    'status' in error() &&
    (
      (options?.excludeStatusCode && error()?.status !== statusCode) ||
      (!options?.excludeStatusCode && error()?.status === statusCode)
    )
    &&
    (
      !options?.acceptedErrorCodes ||
      errorContainsCode(error(), options.acceptedErrorCodes)
    )
    &&
    (
      !options?.includedErrorMessage ||
      errorContainsMessage(error(), options.includedErrorMessage)
    )
  ) {
    return true;
  }
  if(
    typeof error === 'object' &&
    error &&
    'status' in error &&
    (
      (options?.excludeStatusCode && error?.status !== statusCode) ||
      (!options?.excludeStatusCode && error?.status === statusCode)
    )
    &&
    (
      !options?.acceptedErrorCodes ||
      errorContainsCode(error, options.acceptedErrorCodes)
    )
    &&
    (
      !options?.includedErrorMessage ||
      errorContainsMessage(error, options.includedErrorMessage)
    )
  ) {
    return true;
  }

  return false;
}

function errorContainsCode(error: object, acceptedErrorCodes: string[]): boolean {
  return !!(
    'errorCode' in error &&
    typeof error.errorCode === 'string' &&
    acceptedErrorCodes.includes(error.errorCode) ||
    'error' in error &&
    error.error &&
    typeof error.error === 'object' &&
    'errorCode' in error.error &&
    typeof error.error.errorCode === 'string' &&
    acceptedErrorCodes.includes(error.error.errorCode)
  );
}

function errorContainsMessage(error: object, includedErrorMessage: string): boolean {
  return !!(
    includedErrorMessage &&
    'message' in error &&
    typeof error.message === 'string' &&
    error.message.includes(includedErrorMessage) ||
    'error' in error &&
    error.error &&
    typeof error.error === 'object' &&
    'message' in error.error &&
    typeof error.error.message === 'string' &&
    error.error.message.includes(includedErrorMessage)
  );
}

export function transformPriceForChartTooltip(
  value: string | number,
  currencyCode: ECurrencyCode = ECurrencyCode.CZK,
  toTwoDecimal?: boolean,
): string {
  const parsedValue = value + '';

  if(+value === 0) {
    return value + '';
  }

  const formatOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: toTwoDecimal ? 2 : 0,
  };

  try {
    return new Intl.NumberFormat('cs-CZ', formatOptions).format(parseLocaleNumber(parsedValue));
  } catch (error) {
    logError(error);
    return value + '';
  }
}

export function parseLocaleNumber(stringNumber: string): number {
  try {
    const { thousandSeparator, decimalSeparator } = getBrowserNumberSeparators();

    return parseFloat(stringNumber
        .replace(new RegExp('\\' + thousandSeparator, 'g'), '')
        .replace(new RegExp('\\' + decimalSeparator), '.')
    );
  } catch (error) {
    logError(error);
    return parseFloat(stringNumber);
  }
}

/**
 * generate "random" number with both separators by browser to determine what separators should be used
 */
export function getBrowserNumberSeparators(): { thousandSeparator: string, decimalSeparator: string } {
  const numberWithGroup = new Intl.NumberFormat().format(11111); // e.g., '11,111'
  const numberWithDecimal = new Intl.NumberFormat().format(1.1); // e.g., '1.1'
  const thousandSeparator = numberWithGroup.match(/\D+/g)?.[0] || '';
  const decimalSeparator = numberWithDecimal.match(/\D+/g)?.[0] || '.';
  return { thousandSeparator, decimalSeparator };
}

// todo - implement some like this for dynamic minDecimal parameter
//   /**
//    * Force display decimal for small percents ranges that would display duplicit labels
//    */
//   private markPercentValuesToView(): void {
//     const yValues = this.chartData.map(d => d.y);
//     const min = Math.min(...yValues);
//     const max = Math.max(...yValues);
//     this.displayFullPercent = max - min < 6;
//   }
export function transformPercent(value: number, handleLessThan = true, minDecimal?: number | null): string {
  if(value === 0) {
    return '0 %';
  } else if(value < 0.001 && value > -0.001 && handleLessThan) {
    return value < 0 ? '> -0.001 %' : '< 0.001 %';
  } else if(value < 0.01 && value > -0.01) {
    return value.toFixed(minDecimal ?? 3) + ' %';
  } else if(value < 0.1 && value > -0.1) {
    return value.toFixed(minDecimal ?? 2) + ' %';
  } else if (value < 1 && value > -1) {
    return value.toFixed(minDecimal ?? 1) + ' %';
  } else {
    return value.toFixed(minDecimal ?? 0) + ' %';
  }
}

export function filterInstrumentsBasedOnProps(filters: IInstrumentsFilter, instruments: IInstrumentDetailFullOut[]): IInstrumentDetailFullOut[] {
  let filtered = [...instruments];

  if (filters.classes.length) {
    filtered = filtered.filter(instrument =>
      filters.classes.includes(instrument.instrumentCategory)
    );
  }

  if (filters.targets.length) {
    filtered = filtered.filter(instrument =>
      filters.targets.includes(instrument.instrumentTarget as EInstrumentTarget)
    );
  }

  if (filters.regions.length) {
    filtered = filtered.filter(instrument =>
      instrument.regionalOrientations?.some(region =>
        filters.regions.includes(region)
      )
    );
  }

  return filtered;
}

export function filterInstrumentsBasedOnSearch(searchTerm: string, instruments: IInstrumentDetailFullOut[]): IInstrumentDetailFullOut[] {
  let filtered = [...instruments];

  if (searchTerm.trim()) {
    const searchLower = searchTerm.toLowerCase().trim();
    filtered = filtered.filter(instrument =>
      instrument.name?.toLowerCase().includes(searchLower) ||
      instrument.humanName?.toLowerCase().includes(searchLower) ||
      instrument.isin?.toLowerCase().includes(searchLower)
    );
  }

  return filtered;
}

export const sortStrategies = (a: IApiThematicStrategy | ICardViewThematicStrategy, b: IApiThematicStrategy | ICardViewThematicStrategy): number => {
  if (a.uiOrder && b.uiOrder) {
    return a.uiOrder - b.uiOrder;
  } else {
    return a.modelStrategyId - b.modelStrategyId;
  }
}

export function groupTransactionsByMonth(transactions: ITransaction[]): ITransactionGroup[] {
  if (!transactions?.length) {
    return [];
  }

  const groups = new Map<string, ITransactionGroup>();

  transactions.forEach(transaction => {
    const date = dayjs(transaction.transactionTimestamp);
    const month = date.month() + 1; // dayjs months are 0-indexed
    const year = date.year();
    const key = `${year}-${month}`;

    if (!groups.has(key)) {
      groups.set(key, {
        month: month.toString(),
        year: year,
        transactions: []
      });
    }

    groups.get(key)!.transactions.push({
      ...transaction,
      transactionTimestamp: dayjs(transaction.transactionTimestamp).toString()
    });
  });

  return Array.from(groups.values()).sort((a, b) => {
    if (a.year !== b.year) {
      return b.year - a.year;
    }
    return parseInt(b.month) - parseInt(a.month);
  });
}

export function getDateFromRange(range: ETransactionFilterPeriod): { from: Dayjs; to: Dayjs } {
  const now = dayjs();

  switch (range) {
    case ETransactionFilterPeriod.FROM_BEGINNING:
      return {
        from: dayjs('1970-01-01'),
        to: now
      };

    case ETransactionFilterPeriod.LAST_30_DAYS:
      return {
        from: now.subtract(30, 'day'),
        to: now
      };

    case ETransactionFilterPeriod.LAST_3_MONTHS:
      return {
        from: now.subtract(3, 'month'),
        to: now
      };

    case ETransactionFilterPeriod.LAST_6_MONTHS:
      return {
        from: now.subtract(6, 'month'),
        to: now
      };

    case ETransactionFilterPeriod.FROM_YEAR_START:
      return {
        from: now.startOf('year'),
        to: now
      };

    case ETransactionFilterPeriod.LAST_YEAR:
      return {
        from: now.subtract(1, 'year').startOf('year'),
        to: now.subtract(1, 'year').endOf('year')
      };

    case ETransactionFilterPeriod.LAST_5_YEARS:
      return {
        from: now.subtract(5, 'year'),
        to: now
      };

    default:
      return { from: now, to: now };
  }
}

export function getRangeFromDate(from: Dayjs, to: Dayjs): ETransactionFilterPeriod {
  const now = dayjs();

  if (from.isSame(dayjs('1970-01-01'), 'day')) return ETransactionFilterPeriod.FROM_BEGINNING;
  if (from.isSame(now.startOf('year'), 'day')) return ETransactionFilterPeriod.FROM_YEAR_START;
  if (from.isSame(now.subtract(1, 'year').startOf('year'), 'day') &&
      to.isSame(now.subtract(1, 'year').endOf('year'), 'day')) return ETransactionFilterPeriod.LAST_YEAR;
  if (from.isSame(now.subtract(30, 'day'), 'day')) return ETransactionFilterPeriod.LAST_30_DAYS;
  if (from.isSame(now.subtract(3, 'month'), 'day')) return ETransactionFilterPeriod.LAST_3_MONTHS;
  if (from.isSame(now.subtract(6, 'month'), 'day')) return ETransactionFilterPeriod.LAST_6_MONTHS;
  if (from.isSame(now.subtract(5, 'year'), 'day')) return ETransactionFilterPeriod.LAST_5_YEARS;

  return ETransactionFilterPeriod.FROM_BEGINNING; // default fallback
}

export const sortInstruments = (direction: ERiskSort) => (a: IInstrumentDetailFullOut, b: IInstrumentDetailFullOut): number => {
  if (a.okSmartBond !== b.okSmartBond) {
    return a.okSmartBond ? -1 : 1;
  } else if (direction === ERiskSort.HIGH_RISK) {
    return b.riskScore - a.riskScore;
  } else if (direction === ERiskSort.LOW_RISK) {
    return a.riskScore - b.riskScore;
  } else {
    // default by id
    return a.instrumentId - b.instrumentId;
  }
}

export function getDevicePlatform(): EApiDevicePlatforms | null {
  if(Capacitor.getPlatform() === ECapacitorPlatforms.ANDROID) {
    return EApiDevicePlatforms.ANDROID;
  } else if (Capacitor.getPlatform() === ECapacitorPlatforms.IOS) {
    return EApiDevicePlatforms.IOS;
  }
  return null;
}

export function getAppHeaders(input: IApiHeadersGetterInput): HttpHeaders {
  return new HttpHeaders({
    'accept': input.accept,
    'Authorization': 'Bearer ' + input.accessToken,
    ...(
      input.isNativeApp ?
      {
        'X-Device-Type': getDevicePlatform() ?? '',
        'X-App-Version': packageInfo.version,
      } :
      {}
    )
  });
}

export function mapPortfolioOverviewToDisplayed(
  portfolio: IPortfolioOverview,
): IPortfolioOverviewDisplayed {
  return {
    portfolioId: portfolio.portfolioId,
    portfolioName: portfolio.name,
    portfolioType: portfolio.portfolioType,
    isDip: portfolio.dip,
    value: portfolio.balance,
    valueChangeAbsolute: portfolio.actualYield,
    valueChangePercentage: portfolio.twrYield,
    portfolioGoal: portfolio.investmentGoal ? investmentGoalTranslateMap[portfolio.investmentGoal].label : null,
    currency: portfolio.currency,
    img: portfolio.image.exists ? portfolio.image.url : null,
    isEfektika: portfolio.efektika,
    scheduledInstrumentRemovalDate: portfolio.scheduledInstrumentRemovalDate
      ? dayjs(portfolio.scheduledInstrumentRemovalDate).format('DD.MM.YYYY')
      : null,
    containesWillBeRemovedInstrument: !!portfolio.scheduledInstrumentRemovalDate,
    exclusive: portfolio.exclusive,
    exclusiveThreshold: portfolio.exclusiveThreshold,
    isInLiquidation: !!portfolio.isInLiquidation,
  };
}
